/**
 * Helper functions for creating and parsing formatted labels
 */

/**
 * Creates a formatted label string
 * @param type - The type of label (e.g., 'area', 'municipality')
 * @param id - The ID value
 * @param name - The name value
 * @returns A formatted string in the format "type:id:name"
 */
export const createLabel = (
  type: string,
  id: number | string,
  name: string
): string => {
  return `${type}:${id}:${name}`;
};

/**
 * Gets the type from a label
 * @param label - The formatted label string
 * @returns The type part of the label, or null if invalid
 */
export const getTypeFromLabel = (label: string): string | null => {
  if (!label || typeof label !== 'string') return null;
  
  const match = label.match(/^([^:]+):/);
  return match ? match[1] : null;
};

/**
 * Extracts the ID from a formatted label
 * @param label - The formatted label string
 * @returns The ID as a string, or null if the label is invalid
 */
export const getIdFromLabel = (label: string): string | null => {
  if (!label || typeof label !== 'string') return null;
  
  const match = label.match(/^[^:]+:(\d+):/);
  return match ? match[1] : null;
};

/**
 * Extracts the name from a formatted label
 * @param label - The formatted label string
 * @returns The name, or null if the label is invalid
 */
export const getNameFromLabel = (label: string): string | null => {
  if (!label || typeof label !== 'string') return null;
  
  const match = label.match(/^[^:]+:\d+:(.*)/);
  return match ? match[1] : null;
};


/**
 * Extracts all components from a label
 * @param label - The formatted label string
 * @returns An object with type, id, and name properties, or null if invalid
 */
export const parseLabel = (label: string): { id: string; name: string; type: string } | null => {
  if (!label || typeof label !== 'string') return null;
  
  const type = getTypeFromLabel(label);
  const id = getIdFromLabel(label);
  const name = getNameFromLabel(label);
  
  if (type && id && name) {
    return { type, id, name };
  }
  
  return null;
};
